import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Check, Clock3, MessageCircle, Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";

export const Route = createFileRoute("/ordenar")({ component: PublicOrder });

type Drink = { id: number; nombre: string; descripcion: string | null; precio_venta: number };
type Item = { id: number; cantidad: number };
type Step = "menu" | "resumen";
const money = (value: number) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(value);

const apiBase = import.meta.env.VITE_API_URL ?? "/api";

function PublicOrder() {
  const [drinks, setDrinks] = useState<Drink[]>([]);
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [items, setItems] = useState<Item[]>([]);
  const [step, setStep] = useState<Step>("menu");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [pickup, setPickup] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const [response, configResponse] = await Promise.all([
          fetch(`${apiBase}/publico/bebidas`, { cache: "no-store" }),
          fetch(`${apiBase}/publico/configuracion`, { cache: "no-store" }),
        ]);
        if (!configResponse.ok) throw new Error("No se pudo consultar la configuración.");
        const config = await configResponse.json();
        if (!config.success) throw new Error("Configuración inválida.");
        if (active) {
          setWhatsappNumber(config.data.whatsapp);
          setEnabled(config.data.habilitado);
        }
        if (!response.ok) throw new Error("No se pudo consultar el catálogo.");
        const json = await response.json();
        if (!json.success || !Array.isArray(json.data)) throw new Error("Respuesta inválida.");
        if (active) setDrinks(json.data);
      } catch {
        if (active) setError("No pudimos cargar las bebidas. Intenta actualizar la página.");
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, []);

  const selected = useMemo(() => items.flatMap((item) => {
    const drink = drinks.find((drink) => drink.id === item.id);
    return drink && item.cantidad > 0 ? [{ ...drink, cantidad: item.cantidad }] : [];
  }), [items, drinks]);
  const count = selected.reduce((sum, item) => sum + item.cantidad, 0);
  const total = selected.reduce((sum, item) => sum + item.cantidad * item.precio_venta, 0);

  function changeQuantity(id: number, delta: number) {
    setItems((current) => {
      const old = current.find((item) => item.id === id)?.cantidad ?? 0;
      const next = Math.max(0, Math.min(99, old + delta));
      return [...current.filter((item) => item.id !== id), ...(next ? [{ id, cantidad: next }] : [])];
    });
  }

  function sendOrder() {
    if (!name.trim() || !phone.trim() || !selected.length || !whatsappNumber || !enabled) return;
    const lines = [
      "*NUEVO PEDIDO - COLD CREW*", "", `*Cliente:* ${name.trim()}`,
      `*Contacto:* ${phone.trim()}`, `*Hora de recolección:* ${pickup || "Por confirmar"}`,
      "", "*Bebidas:*",
      ...selected.map((item) => `${item.cantidad} × ${item.nombre} — ${money(item.cantidad * item.precio_venta)}`),
      "", `*Total estimado: ${money(total)}*`,
      ...(notes.trim() ? ["", `*Indicaciones:* ${notes.trim()}`] : []),
      "", "Solicito confirmar disponibilidad y total de mi pedido.",
    ];
    window.location.href = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(lines.join("\n"))}`;
  }

  return (
    <div className="min-h-screen bg-[#F5F3ED] text-[#111111]" style={{ fontFamily: "Manrope, sans-serif" }}>
      <header className="border-b border-[#DEDCD5] bg-white/80">
        <div className="mx-auto flex max-w-3xl items-center gap-4 px-5 py-4">
          {step === "resumen" && <button onClick={() => setStep("menu")} aria-label="Volver al menú" className="rounded-full p-2 hover:bg-[#F5F3ED]"><ArrowLeft size={20}/></button>}
          <img src="/logo_sin_fondo.png" alt="Cold Crew" className="h-12 w-auto max-w-[110px] object-contain" />
          <div className="ml-auto text-right"><p className="text-xs font-bold tracking-[.15em] text-[#FF6500]">COLD CREW</p><p className="text-xs text-[#777]">PEDIDOS EN LÍNEA</p></div>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 pb-32 pt-8 sm:px-6">
        <h1 className="text-3xl font-extrabold tracking-tight">{step === "menu" ? "Haz tu pedido" : "Tu pedido"}</h1>
        <p className="mt-2 text-sm text-[#777]">{step === "menu" ? "Selecciona tus bebidas y envíanoslo por WhatsApp." : "Revisa los detalles antes de enviarlos por WhatsApp."}</p>

        {step === "menu" ? (
          <section className="mt-7 space-y-3" aria-label="Catálogo de bebidas">
            <div className="mb-4 flex items-center gap-2"><span className="h-[2px] w-5 bg-[#FF6500]"/><span className="text-xs font-bold tracking-widest text-[#999]">01</span><h2 className="font-bold">Bebidas</h2></div>
            {loading && <p className="rounded-xl border bg-white p-6 text-sm text-[#777]">Cargando bebidas...</p>}
            {error && <p role="alert" className="rounded-xl border bg-white p-6 text-sm text-red-700">{error}</p>}
            {!loading && !error && !drinks.length && <p className="rounded-xl border bg-white p-6 text-sm">No hay bebidas disponibles por el momento.</p>}
            {drinks.map((drink) => {
              const quantity = items.find((item) => item.id === drink.id)?.cantidad ?? 0;
              return <article key={drink.id} className="flex items-center justify-between gap-3 rounded-2xl border border-[#DEDCD5] bg-white p-4 sm:p-5">
                <div className="min-w-0"><h3 className="font-bold">{drink.nombre}</h3>{drink.descripcion && <p className="mt-1 text-xs text-[#777]">{drink.descripcion}</p>}<p className="mt-2 text-sm font-semibold text-[#777]">{money(drink.precio_venta)}</p></div>
                <div className="flex shrink-0 items-center gap-3"><button aria-label={`Quitar ${drink.nombre}`} disabled={!quantity} onClick={() => changeQuantity(drink.id, -1)} className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E7E3DC] disabled:opacity-30"><Minus size={16}/></button><span className="w-5 text-center font-bold tabular-nums">{quantity}</span><button aria-label={`Agregar ${drink.nombre}`} onClick={() => changeQuantity(drink.id, 1)} className="flex h-9 w-9 items-center justify-center rounded-full bg-[#111] text-white"><Plus size={18}/></button></div>
              </article>;
            })}
          </section>
        ) : (
          <div className="mt-7 space-y-5">
            <section className="rounded-2xl border border-[#DEDCD5] bg-white p-5"><h2 className="mb-4 font-bold">Resumen de bebidas</h2>
              {selected.map((item) => <div key={item.id} className="flex items-center gap-3 border-b border-[#EAE6DE] py-3 last:border-0"><span className="font-bold">{item.cantidad} ×</span><div className="min-w-0 flex-1"><p className="font-semibold">{item.nombre}</p><p className="text-xs text-[#777]">{money(item.precio_venta)} c/u</p></div><strong className="text-sm">{money(item.cantidad * item.precio_venta)}</strong><button onClick={() => changeQuantity(item.id, -item.cantidad)} aria-label={`Eliminar ${item.nombre}`} className="p-2 text-[#777]"><Trash2 size={17}/></button></div>)}
              <div className="mt-4 flex items-center justify-between"><strong>Total estimado</strong><strong className="text-2xl">{money(total)}</strong></div>
            </section>
            <section className="space-y-4 rounded-2xl border border-[#DEDCD5] bg-white p-5"><h2 className="font-bold">Datos de contacto</h2>
              <label className="block text-sm font-semibold">Tu nombre *<input required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. María López" className="mt-2 w-full rounded-xl border border-[#DEDCD5] bg-[#FAF9F6] p-3 font-normal outline-none focus:border-[#FF6500]"/></label>
              <label className="block text-sm font-semibold">Tu número de WhatsApp *<input required type="tel" maxLength={25} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Ej. 612 123 4567" className="mt-2 w-full rounded-xl border border-[#DEDCD5] bg-[#FAF9F6] p-3 font-normal outline-none focus:border-[#FF6500]"/></label>
              <label className="block text-sm font-semibold">Hora de recolección (opcional)<div className="relative mt-2"><Clock3 className="absolute left-3 top-3.5 text-[#777]" size={18}/><input type="time" value={pickup} onChange={(e) => setPickup(e.target.value)} className="w-full rounded-xl border border-[#DEDCD5] bg-[#FAF9F6] p-3 pl-10 font-normal outline-none focus:border-[#FF6500]"/></div></label>
              <label className="block text-sm font-semibold">Indicaciones adicionales (opcional)<textarea maxLength={300} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observaciones sobre tu pedido" className="mt-2 w-full resize-none rounded-xl border border-[#DEDCD5] bg-[#FAF9F6] p-3 font-normal outline-none focus:border-[#FF6500]"/></label>
            </section>
            <button onClick={sendOrder} disabled={!name.trim() || !phone.trim() || !selected.length || !whatsappNumber || !enabled} className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#FF6500] px-5 py-4 font-bold text-black disabled:cursor-not-allowed disabled:opacity-40"><MessageCircle size={20}/> Enviar pedido por WhatsApp</button>
            {!loading && !enabled && <p role="alert" className="text-center text-sm text-red-700">Los pedidos en línea están temporalmente desactivados.</p>}
            <p className="text-center text-xs text-[#777]">Tu pedido está sujeto a confirmación. Se abrirá WhatsApp para que envíes el mensaje.</p>
          </div>
        )}
      </main>
      {step === "menu" && <div className="fixed inset-x-0 bottom-0 border-t border-[#DEDCD5] bg-[#F5F3ED]/95 p-4 backdrop-blur"><div className="mx-auto max-w-3xl"><button disabled={!count} onClick={() => setStep("resumen")} className="flex w-full items-center justify-between rounded-xl bg-[#111] px-5 py-4 font-bold text-white disabled:opacity-40"><span className="flex items-center gap-2"><ShoppingBag size={19}/> Ver mi pedido ({count})</span><span>{money(total)}</span></button></div></div>}
    </div>
  );
}
